/**
 * /admin — house ops + 2D layout editors.
 * Gate: Puter molochdadev / grudachain (or fleet email / treasury wallet).
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWalletState } from "@/contexts/wallet-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Bot,
  Coins,
  LayoutTemplate,
  Loader2,
  LogIn,
  RefreshCw,
  Send,
  Shield,
  Users,
  Wallet,
} from "lucide-react";
import {
  adminHeaders,
  ADMIN_TREASURY_WALLET,
  describeAllowlist,
  loadStoredAdminHints,
  loginWithGrudgeAdmin,
  resolveAdminSession,
} from "@/lib/admin-auth";
import { isLoggedIn } from "@/lib/grudge-auth";
import {
  defaultLayoutBundle,
  type LayoutBundle,
} from "@shared/admin-layouts";
import {
  fetchPublishedLayouts,
  writeDraftBundle,
  clearDraftBundle,
} from "@/lib/layout-runtime";
import { LayoutEditorsPanel } from "@/components/admin-layout-editors";

const TREASURY = ADMIN_TREASURY_WALLET;

function BalCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="poker-panel flex items-center gap-3 p-4">
      <div className="rounded-lg bg-amber-950/40 p-2 text-amber-300">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-zinc-500">
          {label}
        </div>
        <div className="text-xl font-bold tabular-nums text-amber-200">
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { wallet, connect, refreshBalances } = useWalletState();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const stored = loadStoredAdminHints();
  const [emailOverride, setEmailOverride] = useState(stored.email || "");
  const [handleOverride, setHandleOverride] = useState(stored.handle || "");
  const [puterUser, setPuterUser] = useState(stored.puter || "");
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionReason, setSessionReason] = useState<string | undefined>();
  const [playerQ, setPlayerQ] = useState("");
  const [targetWallet, setTargetWallet] = useState("");
  const [amount, setAmount] = useState("100");
  const [note, setNote] = useState("");
  const [layoutBundle, setLayoutBundle] = useState<LayoutBundle>(() =>
    defaultLayoutBundle(),
  );

  const pk = wallet.publicKey;

  /** Capture id.grudge-studio.com/admin handoff + JWT / Puter (no freeform spoof as primary). */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await resolveAdminSession(pk);
      if (cancelled) return;
      if (res.hints.email) setEmailOverride(res.hints.email);
      if (res.hints.handle) setHandleOverride(res.hints.handle);
      if (res.hints.puter) setPuterUser(res.hints.puter);
      setSessionReady(res.ok);
      setSessionReason(res.reason);
    })();
    return () => {
      cancelled = true;
    };
  }, [pk]);

  const headers = useMemo(
    () =>
      adminHeaders({
        wallet: pk,
        email: emailOverride || undefined,
        handle: handleOverride || puterUser || undefined,
        puter: puterUser || handleOverride || undefined,
      }),
    [pk, emailOverride, handleOverride, puterUser],
  );

  const whoamiQ = useQuery({
    queryKey: ["admin-whoami", pk, emailOverride, handleOverride, puterUser],
    queryFn: async () => {
      const r = await fetch("/api/admin/whoami", { headers });
      return r.json();
    },
    refetchInterval: 30_000,
    enabled: Boolean(emailOverride || handleOverride || puterUser || pk),
  });

  const isAdmin = whoamiQ.data?.admin === true;

  const houseQ = useQuery({
    queryKey: ["admin-house", pk, emailOverride, handleOverride],
    enabled: isAdmin,
    queryFn: async () => {
      const r = await fetch("/api/admin/house", { headers });
      if (!r.ok) throw new Error((await r.json()).error || "house failed");
      return r.json();
    },
    refetchInterval: 15_000,
  });

  const playersQ = useQuery({
    queryKey: ["admin-players", playerQ, pk],
    enabled: isAdmin,
    queryFn: async () => {
      const u = new URL("/api/admin/players", window.location.origin);
      if (playerQ.trim()) u.searchParams.set("q", playerQ.trim());
      u.searchParams.set("limit", "100");
      const r = await fetch(u.pathname + u.search, { headers });
      if (!r.ok) throw new Error("players failed");
      return r.json();
    },
  });

  const layoutsQ = useQuery({
    queryKey: ["admin-layouts", isAdmin],
    enabled: isAdmin,
    queryFn: async () => {
      const r = await fetch("/api/admin/layouts", { headers });
      if (r.ok) {
        const data = await r.json();
        return data.layouts as LayoutBundle;
      }
      // Fall back to public defaults if migration not applied yet
      return fetchPublishedLayouts();
    },
  });

  useEffect(() => {
    if (layoutsQ.data) setLayoutBundle(layoutsQ.data);
  }, [layoutsQ.data]);

  const mut = useMutation({
    mutationFn: async (opts: {
      path: string;
      body: Record<string, unknown>;
      method?: string;
    }) => {
      const r = await fetch(opts.path, {
        method: opts.method || "POST",
        headers,
        body: JSON.stringify(opts.body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.reason || "failed");
      return data;
    },
    onSuccess: (_data, vars) => {
      toast({
        title: "OK",
        description: vars.path.split("/").pop() + " succeeded",
      });
      void qc.invalidateQueries({ queryKey: ["admin-house"] });
      void qc.invalidateQueries({ queryKey: ["admin-players"] });
      void qc.invalidateQueries({ queryKey: ["admin-layouts"] });
      void refreshBalances();
    },
    onError: (e) => {
      toast({
        title: "Admin action failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    },
  });

  const publishLayouts = useMutation({
    mutationFn: async () => {
      writeDraftBundle(layoutBundle);
      const r = await fetch("/api/admin/layouts", {
        method: "PUT",
        headers,
        body: JSON.stringify({
          slots: layoutBundle.slots,
          tables: layoutBundle.tables,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.detail || "publish failed");
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Layouts published",
        description: "Slots + table seats live for all clients via /api/layouts",
      });
      void qc.invalidateQueries({ queryKey: ["admin-layouts"] });
    },
    onError: (e) => {
      toast({
        title: "Publish failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    },
  });

  const amt = Math.floor(Number(amount) || 0);

  return (
    <div className="poker-bg-page min-h-screen text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-amber-500/25 bg-black/60 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700"
              onClick={() => setLocation("/lobby")}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Lobby
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-lg font-bold text-amber-200">
                <Shield className="h-5 w-5" /> Admin · /admin
              </h1>
              <p className="text-[11px] text-zinc-500">
                MolochDaDev / Grudachain · layouts · players · play ledger
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700"
              onClick={() => {
                void whoamiQ.refetch();
                void houseQ.refetch();
                void playersQ.refetch();
                void layoutsQ.refetch();
              }}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 text-black hover:bg-amber-500"
              onClick={() => loginWithGrudgeAdmin("/admin")}
            >
              <LogIn className="mr-1 h-3.5 w-3.5" /> Admin login (Grudge ID)
            </Button>
            {!pk && (
              <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => void connect("phantom")}>
                <Wallet className="mr-1 h-3.5 w-3.5" /> Treasury wallet
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6">
        <section className="poker-panel space-y-3 p-4">
          <h2 className="text-sm font-semibold text-amber-200">
            Access gate · id.grudge-studio.com/admin
          </h2>
          <p className="text-xs text-zinc-400">
            Operators only. Use{" "}
            <strong className="text-amber-200">Grudge ID admin gate</strong>{" "}
            (Puter <code>molochdadev</code> / <code>grudachain</code> or fleet
            admin email). Player{" "}
            <code className="text-zinc-500">/login</code> does not unlock this
            page. Treasury wallet{" "}
            <code className="text-amber-300">{TREASURY.slice(0, 8)}…</code>{" "}
            remains a secondary house path.
          </p>
          <p className="text-[11px] text-zinc-600">{describeAllowlist()}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-amber-600 font-semibold text-black hover:bg-amber-500"
              onClick={() => loginWithGrudgeAdmin("/admin")}
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign in at id.grudge-studio.com/admin
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700"
              onClick={() =>
                window.open(
                  "https://id.grudge-studio.com/admin?redirect_uri=" +
                    encodeURIComponent(
                      `${window.location.origin}/admin`,
                    ),
                  "_blank",
                  "noopener",
                )
              }
            >
              Open gate in new tab
            </Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Input
              placeholder="Handle (from SSO)"
              value={handleOverride}
              readOnly
              className="border-zinc-700 bg-black/40 text-sm opacity-90"
            />
            <Input
              placeholder="Email (from JWT)"
              value={emailOverride}
              readOnly
              className="border-zinc-700 bg-black/40 text-sm opacity-90"
            />
            <Input
              placeholder="Puter (auto)"
              value={puterUser}
              readOnly
              className="border-zinc-700 bg-black/40 text-sm opacity-90"
            />
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <span>
              Wallet:{" "}
              <code className="text-xs text-zinc-300">
                {pk ? `${pk.slice(0, 12)}…` : "none"}
              </code>
            </span>
            <span>
              Grudge JWT:{" "}
              <code className="text-xs text-zinc-300">
                {isLoggedIn() ? "present" : "none"}
              </code>
            </span>
            <span>
              Puter:{" "}
              <code className="text-xs text-zinc-300">
                {puterUser || "—"}
              </code>
            </span>
            <span
              className={
                isAdmin ? "font-bold text-emerald-400" : "text-red-400"
              }
            >
              {whoamiQ.isLoading
                ? "Checking API…"
                : isAdmin
                  ? "ADMIN OK"
                  : whoamiQ.data?.reason ||
                    sessionReason ||
                    "Not admin"}
            </span>
          </div>
        </section>

        {!isAdmin && (
          <div className="space-y-3 py-12 text-center text-sm text-zinc-500">
            <p>
              This surface is gated by{" "}
              <strong className="text-amber-200/90">
                id.grudge-studio.com/admin
              </strong>
              . Sign in there as an allowlisted operator, then return here.
            </p>
            {!sessionReady && sessionReason && (
              <p className="text-xs text-red-300/80">{sessionReason}</p>
            )}
            <Button
              className="bg-amber-600 text-black hover:bg-amber-500"
              onClick={() => loginWithGrudgeAdmin("/admin")}
            >
              Go to admin login
            </Button>
          </div>
        )}

        {isAdmin && (
          <>
            <section className="grid gap-3 sm:grid-cols-3">
              <BalCard
                icon={<Bot className="h-4 w-4" />}
                label="BUDBai agent play"
                value={houseQ.data?.balances?.agentPlayGbux ?? "…"}
              />
              <BalCard
                icon={<Wallet className="h-4 w-4" />}
                label="Admin treasury play"
                value={houseQ.data?.balances?.adminTreasuryPlayGbux ?? "…"}
              />
              <BalCard
                icon={<Coins className="h-4 w-4" />}
                label="Total rake recorded"
                value={houseQ.data?.balances?.totalRakeRecorded ?? "…"}
              />
            </section>

            <Tabs defaultValue="layouts" className="space-y-4">
              <TabsList className="flex h-auto flex-wrap bg-zinc-900">
                <TabsTrigger value="layouts">
                  <LayoutTemplate className="mr-1 h-3.5 w-3.5" /> Layouts 2D
                </TabsTrigger>
                <TabsTrigger value="players">
                  <Users className="mr-1 h-3.5 w-3.5" /> Players
                </TabsTrigger>
                <TabsTrigger value="gbux">Add / remove GBUX</TabsTrigger>
                <TabsTrigger value="send">Send</TabsTrigger>
                <TabsTrigger value="swap">Agent swap</TabsTrigger>
                <TabsTrigger value="rake">Rake → treasury</TabsTrigger>
                <TabsTrigger value="purge">Purge fake</TabsTrigger>
              </TabsList>

              <TabsContent value="layouts" className="poker-panel space-y-3 p-4">
                <h2 className="text-sm font-semibold text-amber-200">
                  2D layout studio
                </h2>
                <p className="text-xs text-zinc-500">
                  Edit slots cabinet plates for{" "}
                  <a className="text-cyan-400 underline" href="/slots">
                    /slots
                  </a>{" "}
                  and poker seat rails. Draft previews in this browser;
                  Publish writes D1 for all players.
                </p>
                <LayoutEditorsPanel
                  bundle={layoutBundle}
                  onBundleChange={(b) => {
                    setLayoutBundle(b);
                    writeDraftBundle(b);
                  }}
                  onPublish={() => publishLayouts.mutate()}
                  publishing={publishLayouts.isPending}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-zinc-500"
                  onClick={() => {
                    clearDraftBundle();
                    toast({ title: "Local draft cleared" });
                  }}
                >
                  Clear local draft
                </Button>
              </TabsContent>

              <TabsContent value="players" className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search wallet or username"
                    value={playerQ}
                    onChange={(e) => setPlayerQ(e.target.value)}
                    className="border-zinc-700 bg-black/40"
                  />
                  <Button
                    variant="outline"
                    className="border-zinc-700"
                    onClick={() => void playersQ.refetch()}
                  >
                    Search
                  </Button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-zinc-800">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-zinc-900 text-zinc-500">
                      <tr>
                        <th className="p-2">Wallet</th>
                        <th className="p-2">User</th>
                        <th className="p-2">Play GBUX</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(playersQ.data?.players ?? []).map(
                        (p: {
                          wallet: string;
                          username: string;
                          playGbux: number;
                          isAgent?: boolean;
                          isTreasury?: boolean;
                        }) => (
                          <tr
                            key={p.wallet}
                            className="border-t border-zinc-900"
                          >
                            <td className="p-2 font-mono text-[11px]">
                              {p.wallet.slice(0, 8)}…{p.wallet.slice(-4)}
                              {p.isAgent && (
                                <span className="ml-1 text-amber-400">
                                  agent
                                </span>
                              )}
                              {p.isTreasury && (
                                <span className="ml-1 text-emerald-400">
                                  treasury
                                </span>
                              )}
                            </td>
                            <td className="p-2">{p.username}</td>
                            <td className="p-2 font-bold text-amber-300">
                              {Number(p.playGbux).toLocaleString()}
                            </td>
                            <td className="p-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => setTargetWallet(p.wallet)}
                              >
                                Select
                              </Button>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="gbux" className="max-w-lg space-y-3">
                <p className="text-xs text-zinc-500">
                  Credit / debit / set server play balance for any wallet.
                </p>
                <Input
                  placeholder="Player wallet"
                  value={targetWallet}
                  onChange={(e) => setTargetWallet(e.target.value)}
                  className="border-zinc-700 bg-black/40 font-mono text-xs"
                />
                <Input
                  type="number"
                  placeholder="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-zinc-700 bg-black/40"
                />
                <Input
                  placeholder="Note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="border-zinc-700 bg-black/40"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/gbux",
                        body: {
                          action: "credit",
                          wallet: targetWallet,
                          amount: amt,
                          note,
                        },
                      })
                    }
                  >
                    Add GBUX
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/gbux",
                        body: {
                          action: "debit",
                          wallet: targetWallet,
                          amount: amt,
                          note,
                        },
                      })
                    }
                  >
                    Remove GBUX
                  </Button>
                  <Button
                    variant="outline"
                    className="border-zinc-700"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/gbux",
                        body: {
                          action: "set",
                          wallet: targetWallet,
                          amount: amt,
                          note,
                        },
                      })
                    }
                  >
                    Set exact
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="send" className="max-w-lg space-y-3">
                <p className="text-xs text-zinc-500">
                  Send play GBUX from agent or treasury inventory to a player.
                </p>
                <Input
                  placeholder="To player wallet"
                  value={targetWallet}
                  onChange={(e) => setTargetWallet(e.target.value)}
                  className="border-zinc-700 bg-black/40 font-mono text-xs"
                />
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-zinc-700 bg-black/40"
                />
                <div className="flex gap-2">
                  <Button
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/send",
                        body: {
                          toWallet: targetWallet,
                          amount: amt,
                          from: "agent",
                          note,
                        },
                      })
                    }
                  >
                    <Send className="mr-1 h-3.5 w-3.5" /> From agent
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/send",
                        body: {
                          toWallet: targetWallet,
                          amount: amt,
                          from: "treasury",
                          note,
                        },
                      })
                    }
                  >
                    From treasury
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="swap" className="max-w-lg space-y-3">
                <p className="text-xs text-zinc-500">
                  Swap play GBUX between BUDBai agent and treasury (or a wallet).
                </p>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-zinc-700 bg-black/40"
                />
                <Input
                  placeholder="Counterparty (blank = treasury)"
                  value={targetWallet}
                  onChange={(e) => setTargetWallet(e.target.value)}
                  className="border-zinc-700 bg-black/40 font-mono text-xs"
                />
                <div className="flex gap-2">
                  <Button
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/agent-swap",
                        body: {
                          direction: "from_agent",
                          amount: amt,
                          wallet: targetWallet || undefined,
                          note,
                        },
                      })
                    }
                  >
                    Agent → counterparty
                  </Button>
                  <Button
                    variant="outline"
                    className="border-zinc-700"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        path: "/api/admin/agent-swap",
                        body: {
                          direction: "to_agent",
                          amount: amt,
                          wallet: targetWallet || undefined,
                          note,
                        },
                      })
                    }
                  >
                    Counterparty → agent
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="rake" className="max-w-lg space-y-3">
                <p className="text-xs text-zinc-500">
                  Move rake from agent play ledger to admin treasury{" "}
                  {TREASURY.slice(0, 10)}…
                </p>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-zinc-700 bg-black/40"
                />
                <Button
                  disabled={mut.isPending}
                  onClick={() =>
                    mut.mutate({
                      path: "/api/admin/rake-transfer",
                      body: { amount: amt },
                    })
                  }
                >
                  {mut.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Transfer rake → treasury
                </Button>
              </TabsContent>

              <TabsContent value="purge" className="max-w-lg space-y-3">
                <p className="text-xs text-amber-200/80">
                  Zero all play balances that have <strong>no</strong> real
                  deposit/payment/admin credit ledger (legacy free 10k starter).
                </p>
                <Button
                  variant="destructive"
                  disabled={mut.isPending}
                  onClick={() =>
                    mut.mutate({
                      path: "/api/admin/purge-fake-gbux",
                      body: {},
                    })
                  }
                >
                  Purge all fake free GBUX
                </Button>
              </TabsContent>
            </Tabs>

            <section className="rounded-xl border border-zinc-800 p-3">
              <h3 className="mb-2 text-xs font-semibold text-zinc-500">
                Recent house ledger
              </h3>
              <div className="max-h-48 space-y-1 overflow-y-auto font-mono text-[11px] text-zinc-400">
                {(houseQ.data?.recent ?? []).map(
                  (e: {
                    id: string;
                    kind: string;
                    amount: number;
                    note?: string;
                  }) => (
                    <div key={e.id} className="border-b border-zinc-900 py-1">
                      {e.kind} {e.amount} · {e.note?.slice(0, 80)}
                    </div>
                  ),
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
